import { Action, Method } from '@/types/general';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { PaymentType } from '@/components/payments/types/beneficiary';
import { PendingPaymentOrderInfo } from '@/components/payments/types/payment';
import { PaymentOrderStatus } from '@/components/transactions/types/paymentOrder';
import { EntitlementAction, EntitlementType, useEntitlementsStore } from '@/store/entitlementsStore';

export default () => {
  const entitlementsStore = useEntitlementsStore();
  const contextSwitchStore = useContextSwitchStore();

  function canEditPendingPaymentOrder(paymentOrder: PendingPaymentOrderInfo): boolean {
    // v1 check logic on client-gw
    // (
    //   status in ["draft", "rejected", "accepted"]
    //     and $not(data.partnerCategory = "casino")
    //     and $$.request.entitlement.body.ownerType != "Partnership")
    //   or (
    //     status in ["approval", "draft"]
    //       and $not(data.partnerCategory = "sme")
    //       and $$.request.entitlement.body.ownerType = "Partnership"
    //       and "capture" in $$.request.entitlement.body.rights
    // )
    if (contextSwitchStore.isPrivateContext) {
      return (
        [PaymentOrderStatus.draft, PaymentOrderStatus.rejected, PaymentOrderStatus.accepted].includes(
          paymentOrder.status
        ) && paymentOrder.paymentOrderData.partnerCategory !== 'casino'
      );
    } else {
      if (paymentOrder.status === PaymentOrderStatus.draft) {
        const isEntitledToEdit = entitlementsStore.isEntitledToType(
          paymentOrder.paymentOrderData.originator.accountUid,
          EntitlementAction.Capture,
          EntitlementType.CashAccount
        );
        return isEntitledToEdit && paymentOrder.paymentOrderData.partnerCategory !== 'sme';
      } else if (paymentOrder.status === PaymentOrderStatus.approval) {
        const isEntitledToApprove = entitlementsStore.isEntitledToType(
          paymentOrder.paymentOrderData.originator.accountUid,
          EntitlementAction.Approve,
          EntitlementType.CashAccount
        );
        return isEntitledToApprove && paymentOrder.paymentOrderData.partnerCategory !== 'sme';
      }
      return false;
    }
  }

  function canApprovePendingPaymentOrder(paymentOrder: PendingPaymentOrderInfo): boolean {
    if (contextSwitchStore.isPrivateContext) {
      return false;
    } else {
      const isEntitledToApprove = entitlementsStore.isEntitledToType(
        paymentOrder.paymentOrderData.originator.accountUid,
        EntitlementAction.Approve,
        EntitlementType.CashAccount
      );
      return isEntitledToApprove && paymentOrder.paymentOrderData.partnerCategory !== 'sme';
    }
  }

  function canDeletePendingPaymentOrder(paymentOrder: PendingPaymentOrderInfo) {
    // v1 check logic on client-gw
    // status != "retired"
    //   and (
    //     $$.request.entitlement.body.ownerType != "Partnership"
    //   ) or (
    //     $$.request.entitlement.body.ownerType = "Partnership"
    //     and "capture" in $$.request.entitlement.body.rights
    //   )
    if (contextSwitchStore.isPrivateContext) {
      return paymentOrder.status !== PaymentOrderStatus.retired;
    } else {
      const isEntitledToDelete = entitlementsStore.isEntitledToType(
        paymentOrder.paymentOrderData.originator.accountUid,
        EntitlementAction.Capture,
        EntitlementType.CashAccount
      );
      return paymentOrder.status !== PaymentOrderStatus.retired && isEntitledToDelete;
    }
  }

  function baseUriForPendingPaymentOrderType(paymentType?: PaymentType | string): string {
    const paymentMapping: { [key: string]: string } = {
      domestic_esr: '/v2/esr-payment-orders/',
      international_wise: '/wise-payment-orders/',
      international_wisebusiness: '/wisebusiness-payment-orders/'
    };

    // if paymentMapping has the paymentType, use it's value to build the uri otherwise use `/iban-payment-order` as default
    const baseUri = (!!paymentType && paymentMapping[paymentType]) || '/v2/iban-payment-orders/';
    return baseUri;
  }

  function uriForPendingPaymentOrder(paymentOrder: PendingPaymentOrderInfo): string {
    return baseUriForPendingPaymentOrderType(paymentOrder.paymentOrderData.paymentType) + paymentOrder.uid;
  }

  function deleteAction(uid: string): Action {
    return {
      uri: `/payment-orders/${uid}`,
      method: Method.delete
    };
  }

  function declineAction(uid: string): Action {
    return {
      uri: `/v2/partnerships/pending-approvals/${uid}/reject`,
      method: Method.post
    };
  }

  function approveAction(uid: string): Action {
    return {
      uri: `/v2/partnerships/pending-approvals/${uid}/approve`,
      method: Method.post
    };
  }
  return {
    canEditPendingPaymentOrder,
    canApprovePendingPaymentOrder,
    canDeletePendingPaymentOrder,
    baseUriForPendingPaymentOrderType,
    uriForPendingPaymentOrder,
    deleteAction,
    declineAction,
    approveAction
  };
};
